import React, { RefObject, useCallback, useEffect } from 'react'

import { useEscapeKey } from '../../hooks/useEscapeKey'

import {
  activeItemIndex,
  focusItemAt,
  focusTrigger,
  queryExpandedRow,
  queryMenuItems,
} from './UserMenu.focus'

export type UseUserMenuKeyboardProps = {
  isOpen: boolean
  /** Key of the row whose submenu is open, or null. */
  openSubmenuKey: string | null
  triggerRef: RefObject<HTMLDivElement | null>
  menuRef: RefObject<HTMLDivElement | null>
  submenuRef: RefObject<HTMLDivElement | null>
  onClose: () => void
  onCloseSubmenu: () => void
}

export type UseUserMenuKeyboardResult = {
  onMenuKeyDown: (event: React.KeyboardEvent) => void
  onSubmenuKeyDown: (event: React.KeyboardEvent) => void
}

/**
 * Keyboard and focus behaviour for {@link UserMenu} and its submenu.
 *
 * `role="menu"` is a composite widget: it takes one stop in the tab sequence,
 * arrow keys move between rows, and Tab leaves. Tab must not cycle within the
 * menu — that is dialog behaviour, and a screen reader announces a menu as a
 * single stop whose items are reached with the arrows.
 *
 * Focus is moved on open because the surface is portaled to the end of
 * `<body>`; left alone, reaching it would mean tabbing through the whole page.
 * Escape and Tab both hand focus back to the trigger so the sequence resumes
 * where the operator actually was, not at the portal.
 */
export const useUserMenuKeyboard = ({
  isOpen,
  openSubmenuKey,
  triggerRef,
  menuRef,
  submenuRef,
  onClose,
  onCloseSubmenu,
}: UseUserMenuKeyboardProps): UseUserMenuKeyboardResult => {
  useEffect(() => {
    if (isOpen) focusItemAt(menuRef.current, 0)
    // menuRef is stable; re-running on it would refocus mid-interaction.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  useEffect(() => {
    if (openSubmenuKey !== null) focusItemAt(submenuRef.current, 0)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSubmenuKey])

  const handleEscapeMenu = useCallback(() => {
    onClose()
    focusTrigger(triggerRef.current)
  }, [onClose, triggerRef])

  const handleEscapeSubmenu = useCallback(() => {
    // Read the row before closing — it loses aria-expanded on the next render.
    const parentRow = queryExpandedRow(menuRef.current)
    onCloseSubmenu()
    parentRow?.focus()
  }, [menuRef, onCloseSubmenu])

  // Registered before the submenu's so that, if both ever opened in one
  // commit, the submenu still lands on top of the shared escape stack and
  // takes the first Escape.
  useEscapeKey(handleEscapeMenu, isOpen)
  useEscapeKey(handleEscapeSubmenu, openSubmenuKey !== null)

  const createKeyDownHandler =
    (containerRef: RefObject<HTMLDivElement | null>, isSubmenu: boolean) =>
    (event: React.KeyboardEvent) => {
      // Read through the ref at event time: on the render that first mounts
      // the portal, `.current` is still null.
      const container = containerRef.current
      const items = queryMenuItems(container)
      if (!items.length) return
      const index = activeItemIndex(items)

      switch (event.key) {
        case 'ArrowDown':
          event.preventDefault()
          focusItemAt(container, index + 1)
          break
        case 'ArrowUp':
          event.preventDefault()
          focusItemAt(container, index - 1)
          break
        case 'Home':
          event.preventDefault()
          focusItemAt(container, 0)
          break
        case 'End':
          event.preventDefault()
          focusItemAt(container, items.length - 1)
          break
        case 'ArrowRight':
          // Only meaningful on a row that owns a submenu; the row's own click
          // handler opens it, and the open effect above moves focus in.
          if (isSubmenu) break
          if (items[index]?.getAttribute('aria-haspopup') !== 'menu') break
          event.preventDefault()
          items[index]?.click()
          break
        case 'ArrowLeft':
          if (!isSubmenu) break
          event.preventDefault()
          handleEscapeSubmenu()
          break
        case 'Tab':
          // Leave rather than trap. Not prevented — the browser continues the
          // sequence from the trigger we just focused.
          onClose()
          focusTrigger(triggerRef.current)
          break
        default:
          break
      }
    }

  return {
    onMenuKeyDown: createKeyDownHandler(menuRef, false),
    onSubmenuKeyDown: createKeyDownHandler(submenuRef, true),
  }
}
